import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';
import { translateInto } from '@theme/utilities';

const selectors = {
  SECTION_ID: 'fn-section-rendering-product-linked',
  FILTER_PARAM: 'filter.p.m.pim.item_code',
  SWATCH_ITEM: '.js-swatch-item',
  LABEL: '.product-linked__label',
};

const attributes = {
  HIDDEN: 'hidden',
};

const limits = {
  INITIAL: 5,
  EXPANDED: 10,
};

/** @type {Map<string, Promise<string | null>>} */
const CACHE = new Map();

/**
 * Linked color swatches that group products by shared item code.
 * Fetches related products via Section Rendering API and dispatches
 * `linked-color:change` events to the parent product-card.
 *
 * @extends {Component}
 */
export class ProductLinked extends Component {
  /** @type {AbortController | null} */
  #abortController = null;
  #expanded = false;
  #fetched = false;
  /** @type {IntersectionObserver | null} */
  #observer = null;
  #radioName = '';

  connectedCallback() {
    super.connectedCallback();
    this.#radioName = `linked-${crypto.randomUUID()}`;

    const initialInput = /** @type {HTMLInputElement | null} */ (
      this.querySelector('input[type="radio"]')
    );

    if (initialInput) {
      initialInput.name = this.#radioName;
      initialInput.checked = true;
    }

    if (this.dataset.itemCode) {
      this.#setupObserver();
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController?.abort();
    this.#observer?.disconnect();
  }

  #setupObserver() {
    this.#observer = new IntersectionObserver(
      (entries) => {
        if (!entries[0]?.isIntersecting || this.#fetched) return;

        this.#fetchLinkedSwatches();
        this.#observer?.disconnect();
      },
      { rootMargin: `${Math.round(window.innerHeight)}px` },
    );
    this.#observer.observe(this);
  }

  async #fetchLinkedSwatches() {
    const { itemCode, productId } = this.dataset;

    if (!itemCode) return;

    this.#fetched = true;

    try {
      if (!CACHE.has(itemCode)) {
        CACHE.set(itemCode, this.#fetchSection(itemCode));
      }

      const html = await CACHE.get(itemCode);

      if (html) this.#renderSwatches(html, productId, itemCode);
    } catch (error) {
      CACHE.delete(itemCode);

      if (error.name === 'AbortError') return;

      console.error(error);
    }
  }

  /** @param {string} itemCode */
  async #fetchSection(itemCode) {
    this.#abortController = new AbortController();

    // collections ignores filters when there are more than 5k results; search allows up to 100k products before ignoring filters
    const url = new URL(Theme.routes.search_url, location.origin);

    url.searchParams.set('q', '*');
    url.searchParams.set('type', 'product');
    url.searchParams.set(selectors.FILTER_PARAM, itemCode);
    url.searchParams.set('filter.v.availability', '1');
    url.searchParams.set('section_id', selectors.SECTION_ID);

    const response = await fetch(url, { signal: this.#abortController.signal });

    return response.ok ? response.text() : null;
  }

  /**
   * @param {string} html
   * @param {string | undefined} currentProductId
   * @param {string} itemCode
   */
  #renderSwatches(html, currentProductId, itemCode) {
    const items = [
      ...new DOMParser().parseFromString(html, 'text/html').querySelectorAll(selectors.SWATCH_ITEM),
    ];

    /*
      Previously, we were able to use Liquid to filter the items by itemCode. However, there is a
      platform limitation preventing liquid from parsing the result.filter.active_values - possibly
      due to the number of possible values.
    
      JS is now responsible for filtering the items using the DOM. 
    */
    if (itemCode) {
      try {
        // Use subtraction to prevent index reordering issues
        for (let i = items.length - 1; i >= 0; i--) {
          let element = items[i];
          if (!element) continue;
          if (element?.getAttribute('data-item-code') !== itemCode) {
            items.splice(i, 1);
          }
        }
      } catch (err) {
        console.error('Was not able to parse swatches to filter by itemCode correctly', err);
      }
    }

    if (this.dataset.redirect && currentProductId) {
      const hasCurrentProduct = items.some(
        (item) => item.querySelector('input')?.value === currentProductId,
      );
      const unavailableSwatch = !hasCurrentProduct && this.querySelector(selectors.SWATCH_ITEM);

      if (unavailableSwatch) items.push(unavailableSwatch);
    }

    if (items.length <= 1) return;

    const sorted = items.sort((a, b) =>
      (a.getAttribute('title') ?? '').localeCompare(b.getAttribute('title') ?? ''),
    );

    const container = this.refs.items instanceof HTMLElement ? this.refs.items : this;

    if (this.dataset.redirect) {
      this.querySelectorAll(selectors.SWATCH_ITEM).forEach((el) => el.remove());

      for (const item of sorted) {
        const input = /** @type {HTMLInputElement} */ (item.querySelector('input'));

        if (!input) continue;

        input.name = this.#radioName;
        input.checked = input.value === currentProductId;
        container.appendChild(item);
      }

      return;
    }

    const moreButton = this.refs.moreButton instanceof HTMLElement ? this.refs.moreButton : null;
    const others = sorted.filter((item) => item.querySelector('input')?.value !== currentProductId);

    for (const [index, item] of others.entries()) {
      const input = /** @type {HTMLInputElement} */ (item.querySelector('input'));
      input.name = this.#radioName;

      if (index >= limits.INITIAL - 1) item.setAttribute(attributes.HIDDEN, '');

      moreButton?.before(item);
    }

    const hiddenCount = Math.max(0, others.length - (limits.INITIAL - 1));

    if (hiddenCount > 0 && moreButton) {
      moreButton.textContent = `+${hiddenCount}`;
      moreButton.hidden = false;
    }
  }

  handleMoreClick() {
    if (this.#expanded) {
      const url = /** @type {HTMLInputElement | null} */ (
        this.querySelector('input[type="radio"]:checked')
      )?.dataset.productUrl;

      if (url) window.location.href = url;

      return;
    }

    const hidden = [...this.querySelectorAll(`${selectors.SWATCH_ITEM}[${attributes.HIDDEN}]`)];
    const toShow = limits.EXPANDED - limits.INITIAL;

    hidden.slice(0, toShow).forEach((el) => el.removeAttribute(attributes.HIDDEN));

    const remaining = hidden.length - toShow;
    const moreButton = this.refs.moreButton instanceof HTMLElement ? this.refs.moreButton : null;

    if (remaining > 0) {
      this.#expanded = true;
      if (moreButton) moreButton.textContent = `+${remaining}`;
    } else {
      moreButton?.remove();
    }
  }

  /** @param {Event} event */
  handleSwatchChange(event) {
    const input = /** @type {HTMLInputElement} */ (event.target);

    if (!input.dataset.productUrl) return;

    if (this.dataset.redirect) {
      window.location.href = input.dataset.productUrl;

      return;
    }

    // product-linked is preserved across the quick-add section swap, so the label
    // won't be re-rendered server-side — update it here.
    if (input.dataset.color != null) {
      translateInto(
        /** @type {HTMLElement | null} */ (this.querySelector(selectors.LABEL)),
        input.dataset.color,
      );
    }

    this.dispatchEvent(
      new CustomEvent(ThemeEvents.linkedColorChange, {
        bubbles: true,
        detail: { ...input.dataset },
      }),
    );
  }
}

if (!customElements.get('product-linked')) {
  customElements.define('product-linked', ProductLinked);
}
